import React, { useState, useEffect } from 'react'
import { Modal, Form, Button, message, Typography } from 'antd'
import moment from 'moment'
import { DATE_FORMAT } from '../../utils/constants'
import { mockSuppliers, mockCategories } from '../../utils/mockInventoryData'
import BasicInformationSection from './BasicInformationSection'
import StockInformationSection from './StockInformationSection'
import SupplierLocationSection from './SupplierLocationSection'
import SupplierInfoPanel from './SupplierInfoPanel'
import styles from './AddItemModal.module.css'

const { Title } = Typography

const AddItemModal = ({ visible, onCancel, onSave, title, item }) => {
  const [form] = Form.useForm()
  const [suppliers, setSuppliers] = useState(mockSuppliers)
  const [loading, setLoading] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [inventoryTrackingMethod, setInventoryTrackingMethod] = useState('wholePacksOnly')
  const [isTrackedByPacks, setIsTrackedByPacks] = useState(false)

  // Add a state to track price/cost updates from MedPrax
  const [medpraxPrice, setMedpraxPrice] = useState(null)

  // Reset form when modal opens or item changes
  useEffect(() => {
    if (visible) {
      form.resetFields()
      // Clear search results when modal opens
      setSearchResults([])

      if (item) {
        // Format dates for the form
        const formattedItem = {
          ...item,
          expiryDate: item.expiryDate ? moment(item.expiryDate) : null,
          supplierId: item.supplier ? item.supplier.id : null
        }

        form.setFieldsValue(formattedItem)
        setSelectedSupplier(item.supplier)
        setIsTrackedByPacks(item.isTrackedByPacks || false)
        setInventoryTrackingMethod(item.inventoryTrackingMethod || 'wholePacksOnly')
      } else {
        setSelectedSupplier(null)
        setIsTrackedByPacks(false)
        setInventoryTrackingMethod('wholePacksOnly')
      }
    }
  }, [visible, item, form])

  // Listen for price changes from MedPrax and synchronize to form
  useEffect(() => {
    if (medpraxPrice !== null) {
      form.setFieldsValue({ unitCost: medpraxPrice })

      // Reset after applying to prevent repeated setting
      setTimeout(() => setMedpraxPrice(null), 100)
    }
  }, [medpraxPrice, form])

  // Handle supplier change
  const handleSupplierChange = (value) => {
    const supplier = suppliers.find((sup) => sup.id === value)
    setSelectedSupplier(supplier)
  }

  // Format form values before saving
  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setLoading(true)

        try {
          // Format dates back to strings
          const formattedValues = {
            ...values,
            expiryDate: values.expiryDate ? values.expiryDate.format(DATE_FORMAT) : null,
            // Ensure tracking method is included
            isTrackedByPacks: isTrackedByPacks,
            inventoryTrackingMethod: inventoryTrackingMethod,
            // Initialize partial units count if not already set
            partialUnitsUsed: values.partialUnitsUsed || 0
          }

          // Add supplier object for consistency
          const supplierObj = suppliers.find((sup) => sup.id === values.supplierId) || null
          formattedValues.supplier = supplierObj

          // If editing, preserve the ID
          if (item) {
            formattedValues.id = item.id
          }

          // Save the item
          const success = onSave(formattedValues)

          if (success) {
            message.success(`${item ? 'Updated' : 'Added'} inventory item successfully`)
            form.resetFields()
          }
        } catch (error) {
          message.error(`Error ${item ? 'updating' : 'adding'} inventory item: ${error.message}`)
        } finally {
          setLoading(false)
        }
      })
      .catch((info) => {})
  }

  // Calculate available units for displaying
  const calculateAvailableUnits = () => {
    try {
      // Get the current form values
      const formValues = form.getFieldsValue(['packSize', 'quantityOnHand', 'partialUnitsUsed'])
      const packSize = formValues.packSize || 1
      const quantityOnHand = formValues.quantityOnHand || 0
      const partialUnitsUsed = formValues.partialUnitsUsed || 0

      // Calculate based on tracking method
      if (inventoryTrackingMethod === 'wholePacksOnly') {
        // Only whole packs are considered
        return quantityOnHand * packSize
      } else if (inventoryTrackingMethod === 'partialPacks') {
        // Consider partial packs
        return quantityOnHand * packSize - partialUnitsUsed
      } else {
        // Track by individual units
        return quantityOnHand
      }
    } catch (error) {
      console.error('Error calculating available units:', error)
      return 0
    }
  }

  // Add a handler for the cancel operation
  const handleCancel = () => {
    // Clear search results and reset form
    setSearchResults([])
    onCancel()
  }

  return (
    <Modal
      title={<div className={styles.modalHeader}>{title}</div>}
      visible={visible}
      onCancel={handleCancel}
      width={1200}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      footer={
        <div className={styles.modalFooter}>
          <Button key='cancel' onClick={handleCancel} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button key='save' type='primary' loading={loading} onClick={handleSubmit} className={styles.submitButton}>
            Save
          </Button>
        </div>
      }
      className={styles.customModal}
    >
      <Form
        form={form}
        layout='vertical'
        name='inventoryItemForm'
        initialValues={{
          quantityOnHand: 0,
          minStockLevel: 10,
          maxStockLevel: 100,
          unitCost: 0,
          packSize: 1,
          isTrackedByPacks: false,
          inventoryTrackingMethod: 'wholePacksOnly',
          partialUnitsUsed: 0,
          reorderType: 'manual'
        }}
        className={styles.formContainer}
      >
        <div className={styles.basicInfoSection}>
          <BasicInformationSection
            form={form}
            isSearching={isSearching}
            setIsSearching={setIsSearching}
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            inventoryTrackingMethod={inventoryTrackingMethod}
            setInventoryTrackingMethod={setInventoryTrackingMethod}
            isTrackedByPacks={isTrackedByPacks}
            setIsTrackedByPacks={setIsTrackedByPacks}
            item={item}
            setMedpraxPrice={setMedpraxPrice}
            styles={styles}
          />
        </div>

        <div className={styles.stockInfoSection}>
          <StockInformationSection
            form={form}
            inventoryTrackingMethod={inventoryTrackingMethod}
            setInventoryTrackingMethod={setInventoryTrackingMethod}
            isTrackedByPacks={isTrackedByPacks}
            setIsTrackedByPacks={setIsTrackedByPacks}
            calculateAvailableUnits={calculateAvailableUnits}
            styles={styles}
          />
        </div>

        <div className={styles.supplierSection}>
          <SupplierLocationSection form={form} handleSupplierChange={handleSupplierChange} styles={styles} />
        </div>
      </Form>

      {selectedSupplier && (
        <div className={styles.supplierInfo}>
          <SupplierInfoPanel supplier={selectedSupplier} styles={styles} />
        </div>
      )}
    </Modal>
  )
}

export default AddItemModal
